import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { submitContactForm, getContentByType, LegalArea } from '../api/contentApi';

// Default limits shown in UI before settings are loaded
const DEFAULT_MAX_FILES = 5;
const DEFAULT_MAX_SIZE_MB = 10;

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'de';

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    zip: '',
    city: '',
    country: '',
    areaOfLaw: '',
    subject: '',
    message: '',
    preferredLanguage: currentLang,
    consent: false,
    website: '', // honeypot
  });
  const [files, setFiles] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');

  const [legalUrls, setLegalUrls] = useState<{ privacy: string; terms: string; cancellation: string }>({
    privacy: '#',
    terms: '#',
    cancellation: '#',
  });
  const [legalAreas, setLegalAreas] = useState<LegalArea[]>([]);

  useEffect(() => {
    getContentByType('settings').then((docs) => {
      if (docs.length === 0) return;
      const doc = docs[0];
      // For settings, read URLs directly (no isPublished filter on translation level).
      // Fall back to 'de' or the first available language if current lang not configured.
      const trans =
        doc.translations?.[currentLang] ??
        doc.translations?.['de'] ??
        Object.values(doc.translations ?? {})[0];
      if (trans) {
        setLegalUrls({
          privacy: trans.urlPrivacy || '#',
          terms: trans.urlTerms || '#',
          cancellation: trans.urlCancellation || '#',
        });
      }
      if (doc.legalAreas && doc.legalAreas.length > 0) {
        setLegalAreas(doc.legalAreas);
      }
    }).catch(() => {
      // fallback — legal URLs remain '#', no areas shown
    });
  }, [currentLang]);

  const isValidEmail = (value: string) =>
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
    if (name === 'email') setEmailError('');
  };

  const handleEmailBlur = () => {
    if (form.email && !isValidEmail(form.email)) {
      setEmailError(t('contact.emailInvalid'));
    } else {
      setEmailError('');
    }
  };

  const validateFiles = useCallback((newFiles: File[]): string[] => {
    const errs: string[] = [];
    if (newFiles.length > DEFAULT_MAX_FILES)
      errs.push(t('contact.attachmentsHint', { maxFiles: DEFAULT_MAX_FILES, maxSize: DEFAULT_MAX_SIZE_MB }));
    newFiles.forEach((f) => {
      if (f.size > DEFAULT_MAX_SIZE_MB * 1024 * 1024)
        errs.push(`${f.name}: file too large (max ${DEFAULT_MAX_SIZE_MB} MB)`);
    });
    return errs;
  }, [t]);

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    const merged = [...files, ...selected].slice(0, DEFAULT_MAX_FILES);
    setFiles(merged);
    setFileErrors(validateFiles(merged));
  };

  const removeFile = (index: number) => {
    const updated = files.filter((_, i) => i !== index);
    setFiles(updated);
    setFileErrors(validateFiles(updated));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getLegalAreaLabel = (area: LegalArea): string => {
    return (area as any)[currentLang] ?? area.de ?? area.en ?? area.id;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!isValidEmail(form.email)) {
      setEmailError(t('contact.emailInvalid'));
      return;
    }
    if (fileErrors.length > 0) return;
    setSubmitting(true);

    try {
      await submitContactForm(form, files);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || t('contact.errorMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-4 text-center">
        <div className="bg-green-50 border border-green-200 rounded-lg p-8">
          <h2 className="text-2xl font-serif font-bold text-green-800 mb-4">&#10003;</h2>
          <p className="text-green-700 text-lg">{t('contact.successMessage')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('contact.title')} — CLAIRMONT | Rechtsanwalt Marcel Küster</title>
        <html lang={currentLang} />
      </Helmet>

      {/* Hero section — 100vh full bleed. TODO: replace with url('/images/contact.jpg') */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/cd4d63_7c77e61da6b340baa7c7295fa3040632~mv2.avif)` }}
      >
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1>{t('contact.title')}</h1>
        </div>
      </section>

      <div className="max-w-2xl mx-auto py-12 px-4">

        {error && <div className="error mb-6">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot — hidden from real users */}
          <div className="hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              value={form.website}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              {t('contact.name')} *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              placeholder={t('contact.namePlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('contact.email')} *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              required
              placeholder={t('contact.emailPlaceholder')}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-1 outline-none ${
                emailError
                  ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
                  : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
              }`}
            />
            {emailError && (
              <p className="mt-1 text-sm text-red-600">{emailError}</p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              {t('contact.phone')}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder={t('contact.phonePlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>

          <div>
            <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-1">
              {t('contact.street')}
            </label>
            <input
              type="text"
              id="street"
              name="street"
              value={form.street}
              onChange={handleChange}
              placeholder={t('contact.streetPlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-1">
                {t('contact.zip')}
              </label>
              <input
                type="text"
                id="zip"
                name="zip"
                value={form.zip}
                onChange={handleChange}
                placeholder={t('contact.zipPlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </div>
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                {t('contact.city')}
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={form.city}
                onChange={handleChange}
                placeholder={t('contact.cityPlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
              {t('contact.country')}
            </label>
            <input
              type="text"
              id="country"
              name="country"
              value={form.country}
              onChange={handleChange}
              placeholder={t('contact.countryPlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>

          {/* Area of law — only rendered when legalAreas are configured in CMS */}
          {legalAreas.length > 0 && (
            <div>
              <label htmlFor="areaOfLaw" className="block text-sm font-medium text-gray-700 mb-1">
                {t('contact.areaOfLaw')}
              </label>
              <select
                id="areaOfLaw"
                name="areaOfLaw"
                value={form.areaOfLaw}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
              >
                <option value="">{t('contact.areaOfLawPlaceholder')}</option>
                {legalAreas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {getLegalAreaLabel(area)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
              {t('contact.subject')} *
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={form.subject}
              onChange={handleChange}
              required
              placeholder={t('contact.subjectPlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              {t('contact.message')} *
            </label>
            <textarea
              id="message"
              name="message"
              value={form.message}
              onChange={handleChange}
              required
              rows={6}
              placeholder={t('contact.messagePlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none resize-y"
            />
          </div>

          {/* File attachments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('contact.attachments')}
            </label>
            <p className="text-xs text-gray-500 mb-2">
              {t('contact.attachmentsHint', { maxFiles: DEFAULT_MAX_FILES, maxSize: DEFAULT_MAX_SIZE_MB })}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              onChange={handleFilesChange}
              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-sm text-gray-700 bg-gray-50 rounded px-3 py-1">
                    <span className="truncate max-w-xs">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="ml-3 text-red-500 hover:text-red-700 text-xs shrink-0"
                    >
                      {t('contact.removeFile')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {fileErrors.map((e, i) => (
              <p key={i} className="mt-1 text-sm text-red-600">{e}</p>
            ))}
          </div>

          <div>
            <label htmlFor="preferredLanguage" className="block text-sm font-medium text-gray-700 mb-1">
              {t('contact.preferredLanguage')}
            </label>
            <select
              id="preferredLanguage"
              name="preferredLanguage"
              value={form.preferredLanguage}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            >
              <option value="de">{t('language.de')}</option>
              <option value="fr">{t('language.fr')}</option>
              <option value="en">{t('language.en')}</option>
            </select>
          </div>

          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="consent"
              name="consent"
              checked={form.consent}
              onChange={handleChange}
              required
              className="mt-1 h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="consent" className="text-sm text-gray-600">
              {t('contact.consentPrefix')}{' '}
              <a href={legalUrls.privacy} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                {t('contact.consentPrivacyLink')}
              </a>
              {' '}{t('contact.consentSuffix')}{' '}
              {t('contact.consentTermsConnector')}{' '}
              <a href={legalUrls.terms} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                {t('contact.termsLink')}
              </a>
              {' '}{t('contact.consentCancellationConnector')}{' '}
              <a href={legalUrls.cancellation} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                {t('contact.cancellationLink')}
              </a>
              {t('contact.consentEnd')}{' *'}
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting || fileErrors.length > 0}
            className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? t('common.loading') : t('contact.submit')}
          </button>
        </form>
      </div>
    </>
  );
};

export default ContactPage;
